import * as React from 'react'
import ModalLayout from '@/modules/drivers/modals/shared/ModalLayout'
import { Dropdown } from '@/app/features/company-selection/Dropdown'
import { CompanyIcon } from '@/app/features/icons/Company'
import { useQueryCompanies } from '@/modules/shared/query-client'
import { isUnusableServiceCompany } from '@/app/constants/companyType'
import {
  findActiveDatabase,
  isMigrationFailedDatabase,
  isPreparingDatabase,
  isUsableDatabase,
  useQueryCompanyDatabases,
} from '@/lib/api/company/useCompanyDatabaseHooks'
import { CompanyDatabaseModel } from '@/lib/api/company/companyDatabases'
import { useLocation, useNavigate } from 'react-router-dom'
import { Spinner } from '@/ui/feedback/Spinner'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

type Props = {
  companyId: string | null
  onCompanyChange: () => void
}

// Without an identifiable active database — the list is still empty while the company is
// being provisioned, or the caller's per-database access restrictions filtered the active
// row out of the response — we can't tell "broken" from "still migrating", so keep
// waiting. Sending someone to support over a company that is merely migrating is worse
// than a spinner that runs long.
function stillPreparing(databases: CompanyDatabaseModel[] | undefined): boolean {
  if (databases === undefined) return true
  const active = findActiveDatabase(databases)
  if (active === null) return true
  return isPreparingDatabase(active)
}

// Poll while the active database can still become usable on its own (restore or
// migration in flight). A database that is simply unreachable won't fix itself.
const settled = (databases: CompanyDatabaseModel[] | undefined) =>
  !stillPreparing(databases)

export const NotMigratedCompanyModal = ({ companyId, onCompanyChange }: Props) => {
  const { t } = useLocalize()
  const location = useLocation()
  const navigate = useNavigate()

  const { data: databases } = useQueryCompanyDatabases(companyId, {
    pollUntil: settled,
  })
  const companiesQuery = useQueryCompanies({ includeDeleted: false })

  const activeDatabase = React.useMemo(
    () => findActiveDatabase(databases),
    [databases],
  )

  const preparing = stillPreparing(databases)
  const ready = isUsableDatabase(activeDatabase)

  const elapsedSeconds = useElapsedSeconds(preparing)

  const currentCompany = React.useMemo(() => {
    if (!companyId || !companiesQuery.data) return null
    return companiesQuery.data.find((c) => c.id === companyId) ?? null
  }, [companyId, companiesQuery.data])

  // isValid is also true for a company with no database at all, so offering the
  // service company as an escape from a broken one would just swap one 500 for another.
  const otherCompanies = (companiesQuery.data ?? []).filter(
    (c) =>
      c.id !== companyId && c.isValid !== false && !isUnusableServiceCompany(c),
  )

  const companiesOptions = otherCompanies.map((company) => ({
    label: company.companyName || '',
    value: company.id,
    icon: CompanyIcon,
  }))

  const handleChange = (newCompanyId: string) => {
    const newPath = location.pathname.replace(
      /\/company\/([^/]+)/,
      `/company/${newCompanyId}`,
    )
    onCompanyChange()
    navigate(newPath)
  }

  const renderContent = () => {
    if (!companyId) return <UnknownCompanyContent />
    if (preparing) {
      return (
        <MigratingContent
          companyName={currentCompany?.companyName ?? ''}
          elapsedSeconds={elapsedSeconds}
        />
      )
    }
    // The database is usable now, but the screen behind this modal is built from queries
    // that already failed and from an ErrorBoundary that won't reset itself. Reloading is
    // what clears both — as a button, so it can never become a loop.
    if (ready) return <ReadyContent />
    if (isMigrationFailedDatabase(activeDatabase)) {
      return (
        <MigrationFailedContent
          companyName={currentCompany?.companyName ?? ''}
          databaseName={activeDatabase?.databaseName ?? null}
        />
      )
    }
    // Either the database is in a terminal bad state, or it looks fine to the
    // configuration record while the API still refuses it (stale status, a database that
    // was dropped or renamed, credentials that no longer open it). Both need support.
    return (
      <DatabaseUnavailableContent
        companyName={currentCompany?.companyName ?? ''}
        databaseName={activeDatabase?.databaseName ?? null}
      />
    )
  }

  return (
    <ModalLayout>
      <div className="tw-relative tw-flex tw-w-full tw-max-w-[460px] tw-flex-col tw-rounded-lg tw-bg-base-100 tw-p-6 tw-shadow-xl">
        {renderContent()}

        {companiesOptions.length > 0 && (
          <div className="tw-mt-6 tw-border-t tw-border-base-300 tw-pt-4">
            <p className="tw-mb-2 tw-text-xs tw-text-content-subtle">
              {t('companyDatabase.switch-company')}
            </p>
            <Dropdown
              icon={CompanyIcon}
              placeholder={t('companyDatabase.select-company')}
              options={companiesOptions}
              name="company"
              value={null}
              onChange={handleChange}
            />
          </div>
        )}
      </div>
    </ModalLayout>
  )
}

const COMPANY_NAME_MARK = '\u0000'

// Nazwa firmy jest wyróżniona, więc zdanie tniemy w miejscu placeholdera.
const CompanySentence = ({
  companyName,
  textKey,
  fallbackKey,
}: {
  companyName: string
  textKey: string
  fallbackKey: string
}) => {
  const { t } = useLocalize()
  if (!companyName) return <>{t(fallbackKey)}</>
  const [before, after = ''] = t(textKey, {
    companyName: COMPANY_NAME_MARK,
  }).split(COMPANY_NAME_MARK)
  return (
    <>
      {before}
      <span className="tw-font-medium tw-text-content-base">{companyName}</span>
      {after}
    </>
  )
}

const DatabaseNameLine = ({ databaseName }: { databaseName: string | null }) => {
  const { t } = useLocalize()
  if (!databaseName) return null
  return (
    <p className="tw-mt-2 tw-text-xs tw-text-content-subtle">
      {t('companyDatabase.database-name')}{' '}
      <span className="tw-font-mono">{databaseName}</span>
    </p>
  )
}

const MigratingContent = ({
  companyName,
  elapsedSeconds,
}: {
  companyName: string
  elapsedSeconds: number
}) => {
  const { t } = useLocalize()
  return (
    <>
      <div className="tw-mb-4 tw-flex tw-items-center tw-gap-3">
        <Spinner size="md" />
        <h2 className="tw-text-lg tw-font-semibold tw-text-content-base">
          {t('companyDatabase.migrating-title')}
        </h2>
      </div>
      <p className="tw-text-sm tw-text-content-secondary">
        <CompanySentence
          companyName={companyName}
          textKey="companyDatabase.migrating-text"
          fallbackKey="companyDatabase.migrating-text-no-company"
        />
      </p>
      <p className="tw-mt-2 tw-text-xs tw-text-content-subtle">
        {t('companyDatabase.migrating-notice')}
        {elapsedSeconds > 0 ? ` · ${elapsedSeconds}s` : ''}.
      </p>
    </>
  )
}

const ReadyContent = () => {
  const { t } = useLocalize()
  return (
    <>
      <h2 className="tw-mb-2 tw-text-lg tw-font-semibold tw-text-content-base">
        {t('companyDatabase.ready-title')}
      </h2>
      <p className="tw-text-sm tw-text-content-secondary">
        {t('companyDatabase.ready-text')}
      </p>
      <RefreshPageButton label={t('companyDatabase.refresh-page')} />
    </>
  )
}

const RefreshPageButton = ({ label }: { label: string }) => (
  <button
    type="button"
    onClick={() => window.location.reload()}
    className="tw-mt-4 tw-cursor-pointer tw-self-start tw-rounded tw-bg-blue-600 tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-white hover:tw-bg-blue-700"
  >
    {label}
  </button>
)

const DatabaseUnavailableContent = ({
  companyName,
  databaseName,
}: {
  companyName: string
  databaseName: string | null
}) => {
  const { t } = useLocalize()
  return (
    <>
      <h2 className="tw-mb-2 tw-text-lg tw-font-semibold tw-text-content-base">
        {t('companyDatabase.unavailable-title')}
      </h2>
      <p className="tw-text-sm tw-text-content-secondary">
        <CompanySentence
          companyName={companyName}
          textKey="companyDatabase.unavailable-text"
          fallbackKey="companyDatabase.unavailable-text-no-company"
        />
      </p>
      <p className="tw-mt-2 tw-text-sm tw-text-content-secondary">
        {t('companyDatabase.contact-support')}
      </p>
      <DatabaseNameLine databaseName={databaseName} />
      <RefreshPageButton label={t('companyDatabase.retry')} />
    </>
  )
}

const MigrationFailedContent = ({
  companyName,
  databaseName,
}: {
  companyName: string
  databaseName: string | null
}) => {
  const { t } = useLocalize()
  return (
    <>
      <h2 className="tw-mb-2 tw-text-lg tw-font-semibold tw-text-content-base">
        {t('companyDatabase.migration-failed-title')}
      </h2>
      <p className="tw-text-sm tw-text-content-secondary">
        <CompanySentence
          companyName={companyName}
          textKey="companyDatabase.migration-failed-text"
          fallbackKey="companyDatabase.migration-failed-text-no-company"
        />
      </p>
      <p className="tw-mt-2 tw-text-sm tw-text-content-secondary">
        {t('companyDatabase.migration-failed-contact')}
      </p>
      <DatabaseNameLine databaseName={databaseName} />
      <RefreshPageButton label={t('companyDatabase.retry')} />
    </>
  )
}

const UnknownCompanyContent = () => {
  const { t } = useLocalize()
  return (
    <>
      <h2 className="tw-mb-2 tw-text-lg tw-font-semibold tw-text-content-base">
        {t('companyDatabase.unknown-title')}
      </h2>
      <p className="tw-text-sm tw-text-content-secondary">
        {t('companyDatabase.unknown-text')}
      </p>
    </>
  )
}

function useElapsedSeconds(active: boolean) {
  const [elapsed, setElapsed] = React.useState(0)
  React.useEffect(() => {
    if (!active) {
      setElapsed(0)
      return undefined
    }
    const start = Date.now()
    const id = setInterval(() => {
      setElapsed(Math.floor((Date.now() - start) / 1000))
    }, 1000)
    return () => clearInterval(id)
  }, [active])
  return elapsed
}
